import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showModal } from "../statusModal/statusModal";
import {
  fetchChatMessagesAPI,
  fetchChatsAPI,
  markMessagesAsReadByAdminAPI,
  sendSupportMessageAPI,
  updateChatAccessAPI,
} from "./supportChatAPI";

const initialState = {
  chats: [],
  fetchAllChatsStatus: "idle",
  fetchAllChatsError: null,

  messages: {},
  messagesStatus: {},
  messagesError: {},

  activeChatId: null,
  activeUserId: null,

  chatsPagination: {
    page: 1,
    limit: 10,
    hasMore: true,
  },
};

export const fetchSupportChats = createAsyncThunk(
  "supportChat/fetchSupportChats",
  async ({ page, limit, username } = {}, { getState, rejectWithValue }) => {
    const state = getState().supportChat;
    const actualPage = page || state.chatsPagination.page;
    const actualLimit = limit || state.chatsPagination.limit;

    try {
      const res = await fetchChatsAPI(actualPage, actualLimit, username);
      return {
        chats: res.data.chats,
        hasMore: res.data.hasMore,
        page: actualPage,
      };
    } catch (error) {
      return rejectWithValue("Failed to fetch support chats");
    }
  }
);

export const fetchSupportMessages = createAsyncThunk(
  "supportChat/fetchMessages",
  async ({ chatId, beforeId = null, limit = 30 }, { rejectWithValue }) => {
    try {
      const res = await fetchChatMessagesAPI(chatId, { beforeId, limit });

      return {
        chatId,
        messages: res.data.messages,
        hasMore: res.data.hasMore,
      };
    } catch (error) {
      return rejectWithValue({
        chatId,
        error: error.response?.data?.message || "Failed to fetch messages",
      });
    }
  }
);

export const sendSupportMessage = createAsyncThunk(
  "supportChat/sendMessage",
  async ({ chatId, message }, { rejectWithValue }) => {
    try {
      const res = await sendSupportMessageAPI(chatId, message);
      return { chatId, message: res.data.message };
    } catch (error) {
      return rejectWithValue({
        chatId,
        error: error.response?.data?.message || "Failed to send message",
      });
    }
  }
);

export const markAsReadByAdmin = createAsyncThunk(
  "supportChat/markAsReadByAdmin",
  async (chatId, { rejectWithValue }) => {
    try {
      await markMessagesAsReadByAdminAPI(chatId);
      return chatId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark messages as read"
      );
    }
  }
);

export const toggleChatAccess = createAsyncThunk(
  "users/toggleChatAccess",
  async ({ userId, access }, { dispatch, rejectWithValue }) => {
    try {
      const updatedUser = await updateChatAccessAPI(userId, access);
      dispatch(
        showModal({
          message: `تم ${access ? "تفعيل" : "تعطيل"} المحادثة لهذا المستخدم`,
          status: "success",
        })
      );

      return updatedUser?.data?.user;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ أثناء تحديث حالة المحادثة",
          status: "error",
        })
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to update chat access"
      );
    }
  }
);

const supportChatSlice = createSlice({
  name: "supportChat",
  initialState,
  reducers: {
    nextChatPage: (state) => {
      if (state.chatsPagination.hasMore) {
        state.chatsPagination.page += 1;
      }
    },
    resetChatPagination: (state) => {
      state.chatsPagination.page = 1;
      state.chatsPagination.hasMore = true;
    },

    setActiveSupportChat: (state, action) => {
      state.activeChatId = action.payload;
    },
    setActiveUserId: (state, action) => {
      state.activeUserId = action.payload;
    },

    addSocketMessage: (state, action) => {
      const { chatId, message } = action.payload;

      if (!state.messages[chatId]) {
        state.messages[chatId] = { items: [], isLoading: false, hasMore: true };
      }

      const exists = state.messages[chatId].items.some(
        (m) => m._id === message._id
      );

      if (!exists) {
        state.messages[chatId].items.push(message);
      }

      const chatIndex = state.chats.findIndex((chat) => chat?._id === chatId);
      if (chatIndex !== -1) {
        const updatedChat = {
          ...state.chats[chatIndex],
          lastMessage: message.message,
          unreadByAdminCount: message.senderId
            ? state.chats[chatIndex].unreadByAdminCount + 1
            : state.chats[chatIndex].unreadByAdminCount,
        };

        state.chats.splice(chatIndex, 1);
        state.chats.unshift(updatedChat);
      }
    },

    nextMessagesPage: (state) => {
      if (state.messagesPagination.hasMore) {
        state.messagesPagination.page += 1;
      }
    },
    resetMessagesPagination: (state) => {
      state.messagesPagination = {
        page: 1,
        limit: 25,
        hasMore: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetching all chats
      .addCase(fetchSupportChats.pending, (state) => {
        state.fetchAllChatsStatus = "loading";
        state.fetchAllChatsError = null;
      })
      .addCase(fetchSupportChats.fulfilled, (state, action) => {
        const { chats, hasMore, page } = action.payload;

        state.fetchAllChatsStatus = "succeeded";
        state.chatsPagination.page = page;
        state.chatsPagination.hasMore = hasMore;

        if (page === 1) {
          state.chats = chats;
        } else {
          const existingIds = new Set(state.chats.map((chat) => chat._id));
          const newChats = chats.filter((chat) => !existingIds.has(chat._id));
          state.chats.push(...newChats);
        }
      })
      .addCase(fetchSupportChats.rejected, (state, action) => {
        state.fetchAllChatsStatus = "failed";
        state.fetchAllChatsError = action.payload;
      })

      .addCase(fetchSupportMessages.pending, (state, action) => {
        const { chatId } = action.meta.arg;
        if (!state.messages[chatId]) {
          state.messages[chatId] = {
            items: [],
            isLoading: false,
            hasMore: true,
          };
        }
        state.messages[chatId].isLoading = true;
      })
      .addCase(fetchSupportMessages.fulfilled, (state, action) => {
        const { chatId, messages, hasMore } = action.payload;

        if (!state.messages[chatId]) {
          state.messages[chatId] = {
            items: [],
            isLoading: false,
            hasMore: true,
          };
        }

        const existingIds = new Set(
          state.messages[chatId].items.map((m) => m._id)
        );

        const newMessages = messages.filter((msg) => !existingIds.has(msg._id));

        state.messages[chatId].items.unshift(...newMessages);
        state.messages[chatId].hasMore = hasMore;
        state.messages[chatId].isLoading = false;
      })
      .addCase(fetchSupportMessages.rejected, (state, action) => {
        const { chatId, error } = action.payload;
        state.messagesStatus[chatId] = "failed";
        state.messagesError[chatId] = error;
      })

      .addCase(sendSupportMessage.pending, (state, action) => {
        const chatId = action.meta.arg.chatId;
        state.messagesStatus[chatId] = "loading";
        state.messagesError[chatId] = null;
      })
      .addCase(sendSupportMessage.fulfilled, (state, action) => {
        const { chatId } = action.payload;
        state.messagesStatus[chatId] = "succeeded";
      })
      .addCase(sendSupportMessage.rejected, (state, action) => {
        const fallbackChatId = action.meta?.arg?.chatId;
        const chatId = action.payload?.chatId || fallbackChatId;
        const error =
          action.payload?.error ||
          action.error?.message ||
          "Something went wrong";

        if (!chatId) return;

        state.messagesStatus[chatId] = "failed";
        state.messagesError[chatId] = error;
      })

      .addCase(markAsReadByAdmin.fulfilled, (state, action) => {
        const chatId = action.payload;

        if (state.messages[chatId]) {
          state.messages[chatId].items = state.messages[chatId].items.map(
            (msg) => (msg.senderId && !msg.read ? { ...msg, read: true } : msg)
          );
        }

        const chatIndex = state.chats.findIndex((chat) => chat._id === chatId);
        if (chatIndex !== -1) {
          state.chats[chatIndex].unreadByAdminCount = 0;
        }
      })

      .addCase(toggleChatAccess.pending, (state) => {
        state.chatAccessUpdateStatus = "loading";
        state.error = null;
      })
      .addCase(toggleChatAccess.fulfilled, (state, action) => {
        state.chatAccessUpdateStatus = "succeeded";

        const updatedUser = action.payload;

        state.chats = state.chats.map((chat) => {
          if (chat.userId && chat.userId?._id === updatedUser?._id) {
            return {
              ...chat,
              userId: {
                ...chat.userId,
                chatAccess: updatedUser.chatAccess,
              },
            };
          }
          return chat;
        });

        if (state.activeUserId?._id === updatedUser?._id) {
          state.activeUserId.chatAccess = updatedUser.chatAccess;
        }
      })
      .addCase(toggleChatAccess.rejected, (state, action) => {
        state.chatAccessUpdateStatus = "failed";
        state.error = action.payload;
      });
  },
});

export const {
  setActiveSupportChat,
  addSocketMessage,
  setActiveUserId,
  nextChatPage,
  resetChatPagination,
} = supportChatSlice.actions;

export default supportChatSlice.reducer;
